import {Component, ElementRef, HostListener, OnInit, computed, effect, inject, signal, viewChild} from '@angular/core';
import {AuthenticationService} from '../../../../_auth/authentication.service';
import {PermissionService} from '../../../../_auth/permission.service';
import {SelectComponent} from '../../../../_ui/components/controls/select/select.component';
import {AccountSecurityHttpService} from './account-security-http.service';
import {
  ACCOUNT_SECURITY_ADMIN_LOAD_FALLBACK_MESSAGE,
  ACCOUNT_SECURITY_LEVEL_DESCRIPTIONS,
  ACCOUNT_SECURITY_LEVEL_LABELS,
  ACCOUNT_SECURITY_LEVEL_OPTIONS,
  AccountSecurityLevel,
  describeAccountSecurityAdminError,
  isKnownAccountSecurityLevel,
  raisesRequirement
} from './account-security.models';

/**
 * The company-wide **required account-security level** (#282): what a company insists its members prove at
 * sign-in — a password, a second factor, or an external provider.
 *
 * ### Where it lives, and why
 *
 * A section of `company/settings` (Unternehmenseinstellungen), beside the server timezone and the Dienstplan
 * rounding — the two other settings on that screen that are set **once for the whole company** rather than per
 * shard, per employee or per device. `Company.RequiredAccountSecurityLevel` is a column on `Company`, read on
 * every sign-in and on every context entry, so this is the same kind of object: one value, company-wide,
 * changed rarely and by few. It is not an *account* setting (that is the profile's Zwei-Faktor panel, which
 * enrols one person's authenticator) and not an access grant (that is `company/access`, which is per person).
 *
 * ### A separate component, not another block in `settings.component.html`
 *
 * That component is 571 lines of unrelated state (SumUp keys, Telegram tokens, a 400-entry timezone list) on a
 * constructor-injected, non-signal, `*ngIf`-era shape. This panel owns four pieces of state, a confirmation
 * dialog and an error mapping; folding them in would mean a sixth concern in a component that already cannot
 * be tested for one of its five. As a child it is mounted by one line, gated by one `@if`, and has its own
 * spec and stories.
 *
 * ### Who may use it — narrower than the screen
 *
 * `company/settings` is reachable by a `ShardAdmin` (`Permissions_CompanySettingsComponent`). The two
 * endpoints behind this panel are not: `AccountController` names only `PlatformOwner` and `CompanyOwner`, and
 * says why — a shard's administrator raising this would bind colleagues in shards they have nothing to do
 * with, and lowering it would silently drop the whole company's bar. So the panel gates on its own
 * {@link PermissionService.Permissions_CompanyAccountSecurityPanel} and renders **nothing** otherwise: a
 * control that answers 403 on save is worse than no control at all.
 *
 * It also renders nothing when the session names **no company** — a platform owner outside a tenant context.
 * The company is a route parameter, so there would be nothing to put in it, and `…/companies//security-level`
 * fails as a route rather than as a permission.
 *
 * ### The two things the screen has to say out loud
 *
 * Both are permanently visible rather than tucked into a tooltip, because a three-item dropdown states neither
 * and both change what an administrator would choose:
 *
 * 1. **A raise reaches people who are already using the product** — at their next sign-in, not immediately.
 *    Sessions minted before the change keep working until they expire (`Constants.UserTimeout`, 31 days); this
 *    is written down as an accepted gap on both `AccountSecurityPolicy` and `AccountSecurityGateMiddleware`.
 *    What happens *after* that is not nothing: `UserService.SecondFactorGate` mints a **restricted** session
 *    for a password login with no factor, and `AccountSecurityGateMiddleware` then refuses every path except
 *    the enrolment endpoints, so the person lands on the Zwei-Faktor screen and gets back in by enrolling
 *    (`two-factor/reissue`, no re-login). A native identity entering a context is refused outright instead
 *    (`ContextSessionService.AccountSecurityRefusal`).
 * 2. **Federated accounts already satisfy `TwoFactor`, and `Federated` is not simply "more"** — it forbids
 *    native accounts, which nobody can self-remediate (`AccountSecurityPolicy.CanSelfRemediate` is true only
 *    for `TwoFactor`). Both facts come straight from the enum's XML docs, including the deliberate
 *    simplification that a provider with MFA switched off still counts.
 */
@Component({
  selector: 'company-account-security-requirement',
  standalone: true,
  imports: [SelectComponent],
  template: `
    @if (visible()) {
      <div data-testid="account-security-panel"
           class="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 md:grid-cols-3 md:pr-4">

        <div class="px-4 sm:px-0">
          <h2 class="text-base font-semibold leading-7 text-gray-900">Anmeldesicherheit</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">
            Was dieses Unternehmen von jedem Konto verlangt, das sich anmeldet oder in dieses Unternehmen
            wechselt. Die Einstellung gilt für alle Standorte.
          </p>
        </div>

        <div class="relative bg-white shadow ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
          <div class="space-y-6 px-4 pt-2 pb-4 sm:p-8">

            @if (loading()) {
              <p class="text-sm text-gray-500" data-testid="account-security-loading">Wird geladen …</p>
            } @else {

              <div>
                <label for="account-security-level"
                       class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                  Erforderliche Anmeldesicherheit
                </label>
                <cqme-select id="account-security-level" size="md"
                             [options]="options" [labelKey]="'label'" [valueKey]="'value'"
                             placeholder="Stufe wählen …"
                             ariaLabel="Erforderliche Anmeldesicherheit dieses Unternehmens"
                             [disabled]="saving()"
                             [current]="chosen() ?? undefined"
                             (currentChange)="onPicked($event)"/>

                @if (chosenDescription(); as description) {
                  <p class="mt-2 text-sm leading-6 text-gray-600" data-testid="account-security-description">
                    {{ description }}
                  </p>
                }
                @if (unknownStored(); as raw) {
                  <p class="mt-2 text-sm font-semibold text-yellow-700">
                    Diese Version kennt die gespeicherte Stufe „{{ raw }}“ nicht. Eine Änderung würde sie
                    überschreiben.
                  </p>
                }
              </div>

              <!-- Fact 1: a raise reaches people who are already here — at their NEXT sign-in. -->
              <div data-testid="raise-consequence"
                   class="rounded-2xl border border-theme-primary-100/50 bg-theme-primary-50/50 p-4">
                <h3 class="text-sm font-bold tracking-tight text-gray-800">Was eine Anhebung bewirkt</h3>
                <ul class="mt-2 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-700">
                  <li>
                    Bereits angemeldete Sitzungen laufen unverändert weiter — bis zu 31 Tage, bis ihr
                    Sitzungs-Token abläuft. Die neue Stufe greift ab der nächsten Anmeldung bzw. beim nächsten
                    Wechsel in dieses Unternehmen.
                  </li>
                  <li>
                    Betroffen sind dann nicht nur neue Konten: Wer sich mit Passwort anmeldet und noch keine
                    Authenticator-App eingerichtet hat, landet in einer eingeschränkten Sitzung — erreichbar
                    ist dann nur noch die Einrichtung der Zwei-Faktor-Anmeldung, alles andere wird abgewiesen.
                  </li>
                  <li>
                    Die Person kann das selbst beheben: Nach der Einrichtung ist die Sitzung sofort wieder
                    frei, ohne erneute Anmeldung.
                  </li>
                </ul>
              </div>

              <!-- Fact 2: federated already counts, and "Nur externe Anbieter" is a different decision. -->
              <div data-testid="federated-note" class="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                <h3 class="text-sm font-bold tracking-tight text-gray-800">Externe Anmeldungen</h3>
                <ul class="mt-2 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-700">
                  <li>
                    Konten, die sich über Microsoft, Google oder Apple anmelden, erfüllen
                    „{{ label(levels.TwoFactor) }}“ automatisch und richten hier nichts ein. Dabei wird nicht
                    geprüft, ob der Anbieter tatsächlich einen zweiten Faktor verlangt hat — ein
                    Microsoft-Mandant mit abgeschalteter MFA erfüllt die Anforderung also ebenfalls.
                  </li>
                  <li>
                    „{{ label(levels.Federated) }}“ ist keine Steigerung von „{{ label(levels.TwoFactor) }}“,
                    sondern eine andere Entscheidung: Konten mit E-Mail und Passwort werden vollständig
                    verboten. Wer sich so anmeldet, wird ausgesperrt und kann das nicht selbst beheben —
                    solange kein Zugang über einen externen Anbieter eingerichtet ist.
                  </li>
                </ul>
              </div>

              @if (errorMessage(); as message) {
                <p class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert"
                   data-testid="account-security-error">{{ message }}</p>
              }
              @if (savedMessage(); as message) {
                <p class="rounded-2xl bg-green-50 px-4 py-3 text-sm font-semibold text-green-700" role="status"
                   data-testid="account-security-saved">{{ message }}</p>
              }

              <div class="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
                <button type="button" (click)="reset()" [disabled]="!dirty() || saving()"
                        class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                               py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all
                               duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95
                               disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
                  Verwerfen
                </button>
                <button type="button" (click)="requestSave()" [disabled]="!dirty() || saving()"
                        data-testid="account-security-save"
                        class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                               py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                               transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                               active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                               disabled:grayscale">
                  {{ saving() ? 'Wird gespeichert …' : 'Speichern' }}
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    }

    @if (pending(); as target) {
      <!-- Backdrop click cancels: cancelling costs nothing here, the stored value is untouched either way. -->
      <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
           (click)="cancelSave()">
        <div class="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg
                    sm:rounded-2xl"
             role="alertdialog" aria-modal="true" aria-labelledby="account-security-confirm-heading"
             aria-describedby="account-security-confirm-body" data-testid="account-security-confirm"
             (click)="$event.stopPropagation()">
          <div class="space-y-4 p-4 sm:p-6">

            <h2 id="account-security-confirm-heading" class="text-xl font-bold tracking-tight text-gray-900">
              {{ confirmHeading() }}
            </h2>

            <div id="account-security-confirm-body" class="space-y-3 text-sm leading-6 text-gray-700">
              <p>
                Bisher: <span class="font-semibold text-gray-900">{{ label(stored()) }}</span> —
                neu: <span class="font-semibold text-gray-900">{{ label(target) }}</span>.
              </p>
              <p class="rounded-2xl bg-red-50 px-4 py-3 font-semibold text-red-700" role="alert">
                {{ confirmConsequence() }}
              </p>
            </div>

            <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
              <!-- Cancel is low-emphasis and holds focus: the safe answer should be where a stray Enter lands. -->
              <button type="button" #cancelButton (click)="cancelSave()"
                      data-testid="account-security-confirm-cancel"
                      class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                             py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                             hover:border-gray-300 hover:bg-gray-50 active:scale-95">
                Abbrechen
              </button>
              <button type="button" (click)="confirmSave()" [disabled]="saving()"
                      data-testid="account-security-confirm-accept"
                      class="inline-flex items-center justify-center rounded-2xl bg-red-600 px-5 py-2.5
                             text-sm font-semibold text-white shadow-lg shadow-red-100 transition-all
                             duration-200 hover:bg-red-700 hover:shadow-xl active:scale-95
                             disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
                Ja, ändern
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `
})
export class AccountSecurityRequirementComponent implements OnInit {
  private readonly http = inject(AccountSecurityHttpService);
  private readonly permissions = inject(PermissionService);
  private readonly auth = inject(AuthenticationService);

  /** Exposed to the template so the copy names the levels by their single source of truth. */
  protected readonly levels = AccountSecurityLevel;
  protected readonly options = ACCOUNT_SECURITY_LEVEL_OPTIONS;

  /**
   * The company this panel administers — always the one the session names.
   *
   * `''` for a company-independent principal (a platform owner outside a tenant), which is what
   * {@link visible} turns into "render nothing" rather than a request with an empty path segment.
   */
  private readonly companyId = this.auth.userValue?.company?.id ?? '';

  /** The value stored on the server, as last read or last saved. `null` until the first read succeeds. */
  readonly stored = signal<AccountSecurityLevel | null>(null);

  /** The value in the picker. Diverges from {@link stored} exactly while there is an unsaved change. */
  readonly chosen = signal<AccountSecurityLevel | null>(null);

  /** A stored name this build does not know — shown verbatim rather than silently rendered as `Password`. */
  readonly unknownStored = signal<string | null>(null);

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly savedMessage = signal<string | null>(null);

  /** The level awaiting confirmation, or `null` when no dialog is open. */
  protected readonly pending = signal<AccountSecurityLevel | null>(null);

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  /**
   * Whether the panel exists at all.
   *
   * Both halves are hard requirements rather than a convenience: the role half mirrors the endpoints' own gate
   * (see the class doc), and the company half is what keeps an empty route parameter off the wire.
   */
  protected readonly visible = computed(() =>
    this.companyId !== ''
    && this.permissions.CheckPermission(PermissionService.Permissions_CompanyAccountSecurityPanel()));

  protected readonly dirty = computed(() => this.chosen() !== null && this.chosen() !== this.stored());

  protected readonly chosenDescription = computed(() => {
    const level = this.chosen();
    return level ? ACCOUNT_SECURITY_LEVEL_DESCRIPTIONS[level] : null;
  });

  protected readonly confirmHeading = computed(() => {
    const from = this.stored();
    const to = this.pending();
    if (!to || !from) return 'Anmeldesicherheit ändern?';
    return raisesRequirement(from, to) ? 'Anmeldesicherheit anheben?' : 'Anmeldesicherheit senken?';
  });

  /**
   * The one sentence the dialog puts in red — the consequence of *this* change, not a generic warning.
   *
   * Three cases, because they are genuinely different things to be told: locking out password accounts,
   * restricting colleagues at their next sign-in, and dropping the bar for everybody.
   */
  protected readonly confirmConsequence = computed(() => {
    const from = this.stored();
    const to = this.pending();
    if (!to || !from) return '';

    if (to === AccountSecurityLevel.Federated) {
      return 'Konten mit E-Mail und Passwort können sich danach nicht mehr anmelden und können das nicht '
        + 'selbst beheben.';
    }
    if (raisesRequirement(from, to)) {
      return 'Kolleginnen und Kollegen ohne Authenticator-App landen ab ihrer nächsten Anmeldung in einer '
        + 'eingeschränkten Sitzung, bis sie eine eingerichtet haben.';
    }
    return 'Damit sinkt die Anforderung für alle Konten dieses Unternehmens. Bereits eingerichtete '
      + 'Authenticator-Apps bleiben bestehen und werden weiterhin abgefragt.';
  });

  constructor() {
    // Put the caret on Abbrechen as soon as the dialog appears — the safe default, as on the revoke dialog.
    effect(() => {
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  ngOnInit(): void {
    if (!this.visible()) {
      this.loading.set(false);
      return;
    }

    this.http.get(this.companyId).subscribe({
      next: requirement => {
        this.loading.set(false);
        if (isKnownAccountSecurityLevel(requirement?.requiredLevel)) {
          this.stored.set(requirement.requiredLevel);
          this.chosen.set(requirement.requiredLevel);
          return;
        }
        // A name this build does not know. Deliberately NOT defaulted to Password: showing a company on
        // "Passwort" when it is on something stricter would invite an administrator to "confirm" a downgrade.
        this.unknownStored.set(requirement?.requiredLevel ?? '');
      },
      error: err => {
        this.loading.set(false);
        this.errorMessage.set(
          describeAccountSecurityAdminError(err, ACCOUNT_SECURITY_ADMIN_LOAD_FALLBACK_MESSAGE));
      }
    });
  }

  /** The German label of a level, or an em dash when there is none yet (first load, unknown stored value). */
  protected label(level: AccountSecurityLevel | null): string {
    return level ? ACCOUNT_SECURITY_LEVEL_LABELS[level] : '—';
  }

  /**
   * `cqme-select` declares `currentChange` as `TValue | TValue[] | undefined` even single-valued, because the
   * same component also does `[multiple]`. Normalised once here rather than cast in the template.
   */
  protected onPicked(value: AccountSecurityLevel | AccountSecurityLevel[] | undefined): void {
    const level = Array.isArray(value) ? value[0] : value;
    if (level) this.choose(level);
  }

  /** Select a level without saving it. Public because the spec drives the picker through it. */
  choose(level: AccountSecurityLevel): void {
    this.chosen.set(level);
    this.errorMessage.set(null);
    this.savedMessage.set(null);
  }

  /** Drop an unsaved change. */
  protected reset(): void {
    this.chosen.set(this.stored());
    this.errorMessage.set(null);
    this.savedMessage.set(null);
  }

  /**
   * Ask before saving.
   *
   * A confirmation step is warranted here and is not ceremony: **both directions are company-wide and neither
   * is visible from this screen afterwards.** Raising it can put colleagues into a restricted session at their
   * next sign-in (or, on `Federated`, lock them out with no self-service route at all); lowering it silently
   * drops the bar for every account in the company, and nothing else on the screen would show that it
   * happened. The dialog names the old value, the new value and which of those three things it does — the
   * "name what is at stake" rule from the style guide's destructive-confirmation pattern.
   */
  protected requestSave(): void {
    const level = this.chosen();
    if (!level || level === this.stored() || this.saving()) return;
    this.pending.set(level);
  }

  protected cancelSave(): void {
    this.pending.set(null);
  }

  protected confirmSave(): void {
    const level = this.pending();
    if (!level || this.saving()) return;

    this.saving.set(true);
    this.errorMessage.set(null);

    this.http.set(this.companyId, level).subscribe({
      next: () => {
        this.saving.set(false);
        this.pending.set(null);
        this.stored.set(level);
        this.unknownStored.set(null);
        this.savedMessage.set(`Die Anmeldesicherheit wurde auf „${this.label(level)}“ gespeichert.`);
      },
      error: err => {
        this.saving.set(false);
        this.pending.set(null);
        // `stored` is deliberately untouched: the server refused, so the company is still on the old value and
        // the screen must keep saying so rather than showing the attempted one as if it had taken effect.
        this.errorMessage.set(describeAccountSecurityAdminError(err));
      }
    });
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.pending()) this.cancelSave();
  }
}
